import { dateTime } from "@/lib/format";
import { statusTone } from "@/lib/format";
import type { TrackingEvent } from "@/lib/types";
import { cx } from "./ui";

const dotClasses: Record<string, string> = {
  neutral: "bg-ink-300",
  attention: "bg-brand",
  progress: "bg-brand",
  good: "bg-signal-good",
  warn: "bg-signal-warn",
  bad: "bg-signal-bad",
};

/**
 * Newest first: someone opening a tracking page wants the latest scan, not the
 * history of a parcel they already know was collected.
 */
export function Timeline({ events }: { events: TrackingEvent[] }) {
  if (!events.length) {
    return <p className="text-sm text-ink-400">No scans yet. The first update appears once the carrier collects.</p>;
  }

  const ordered = [...events].sort(
    (a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime(),
  );

  return (
    <ol className="relative space-y-6 border-l border-ink-200 pl-6">
      {ordered.map((event, index) => (
        <li key={`${event.occurred_at}-${index}`} className="relative">
          <span
            className={cx(
              "absolute -left-[1.9rem] top-1 h-3 w-3 rounded-full ring-4 ring-white",
              dotClasses[statusTone[event.status]],
              index === 0 && "ring-brand-soft",
            )}
          />
          <p className={cx("text-sm", index === 0 ? "font-medium" : "text-ink-700")}>
            {event.label || event.description}
          </p>
          <p className="mt-0.5 text-xs text-ink-400">
            {dateTime(event.occurred_at)}
            {event.location ? ` · ${event.location}` : ""}
          </p>
          {event.label && event.description && event.label !== event.description && (
            <p className="mt-1 text-xs text-ink-400">{event.description}</p>
          )}
        </li>
      ))}
    </ol>
  );
}
